import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileCheck, Users, Plus, RefreshCw, Trash2, RefreshCcw, Download, Eye, ClipboardCheck, Calendar as CalIcon, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { API, api, endpoints, getTenantScope } from "@/lib/api";
import { toast } from "sonner";
import { JobPacksTab } from "@/components/audits/JobPacksTab";

const fmt = (s) => s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
const yearOf = (s) => s ? new Date(s).getFullYear() : "—";

async function apiGet(p) { const r = await api.get(p); return r.data; }
async function apiPostForm(p, fd) { const r = await api.post(p, fd, { headers: { "Content-Type": "multipart/form-data" } }); return r.data; }
async function apiDel(p) { const r = await api.delete(p); return r.data; }

export default function AuditsReviewsPage() {
  const scope = getTenantScope();
  const [params] = useSearchParams();
  const qp = params.get("client");
  const [clientSlug, setClientSlug] = useState(qp || scope?.slug || "northwind-corp");
  const [clients, setClients] = useState([]);
  const [audits, setAudits] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState(scope?.role === "auditor" || params.get("tab") === "job-packs" ? "job-packs" : "audits");
  const [packCount, setPackCount] = useState(0);
  const isAuditor = scope?.role === "auditor";
  const [modal, setModal] = useState({ kind: null, edit: null });
  const isClient = ["client", "client_admin", "client_employee"].includes(scope?.role);

  const load = () => {
    if (isAuditor) return;
    endpoints.listClients().then(setClients).catch(() => {});
    apiGet(`/internal-audits?client_slug=${clientSlug}`).then(setAudits).catch(() => setAudits([]));
    apiGet(`/management-reviews?client_slug=${clientSlug}`).then(setReviews).catch(() => setReviews([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientSlug]);
  useEffect(() => {
    if (!qp && !scope?.slug && clients.length && !clients.some((c) => c.slug === clientSlug)) setClientSlug(clients[0].slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  const nextAudit = useMemo(() => audits.filter(a => a.next_due_date).sort((a, b) => (a.next_due_date || "").localeCompare(b.next_due_date || ""))[0], [audits]);
  const nextReview = useMemo(() => reviews.filter(r => r.next_due_date).sort((a, b) => (a.next_due_date || "").localeCompare(b.next_due_date || ""))[0], [reviews]);
  const overdueA = audits.filter(a => a.overdue).length;
  const soonA = audits.filter(a => a.due_soon).length;
  const overdueR = reviews.filter(r => r.overdue).length;
  const soonR = reviews.filter(r => r.due_soon).length;

  const groupByYear = (rows, key) => {
    const g = new Map();
    for (const r of rows) {
      const y = yearOf(r[key]) || "—";
      if (!g.has(y)) g.set(y, []);
      g.get(y).push(r);
    }
    return Array.from(g.entries()).sort((a, b) => Number(b[0]) - Number(a[0]));
  };

  const del = async (path) => { if (!window.confirm("Delete this record? The uploaded file will be removed too.")) return; try { await apiDel(path); toast.success("Deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-600 text-white flex items-center justify-center shadow-md"><ClipboardCheck className="h-5 w-5" /></span>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">{isAuditor ? "Third-Party Audit Prep" : "Internal Audits & Management Reviews"}</h1>
            <p className="text-xs text-slate-500">{isAuditor ? "Job packs shared with you for this audit." : "Upload and track audit reports, reviews and job packs for third-party audits."}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isClient && !isAuditor && (
            <Select value={clientSlug} onValueChange={setClientSlug}>
              <SelectTrigger className="w-48" data-testid="ar-client-select"><SelectValue /></SelectTrigger>
              <SelectContent>{clients.map(c => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
        </div>
      </div>

      {/* Reminders banner */}
      {!isAuditor && <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <ReminderCard tone="indigo" icon={<FileCheck className="h-4 w-4" />} label="Next internal audit"
          date={nextAudit?.next_due_date} sub={nextAudit ? `Last: ${fmt(nextAudit.audit_date)} · ${nextAudit.auditor}` : "No audits recorded yet"}
          overdue={overdueA} soon={soonA} onAdd={() => setModal({ kind: "audit", edit: null })} testid="ar-next-audit" />
        <ReminderCard tone="cyan" icon={<Users className="h-4 w-4" />} label="Next management review"
          date={nextReview?.next_due_date} sub={nextReview ? `Last: ${fmt(nextReview.meeting_date)}` : "No reviews recorded yet"}
          overdue={overdueR} soon={soonR} onAdd={() => setModal({ kind: "review", edit: null })} testid="ar-next-review" />
      </div>}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          {!isAuditor && <TabsTrigger value="audits" data-testid="ar-tab-audits">Internal Audits ({audits.length})</TabsTrigger>}
          {!isAuditor && <TabsTrigger value="reviews" data-testid="ar-tab-reviews">Management Reviews ({reviews.length})</TabsTrigger>}
          <TabsTrigger value="job-packs" data-testid="ar-tab-job-packs">Third-Party Audit Prep ({packCount})</TabsTrigger>
        </TabsList>

        <TabsContent value="job-packs" className="pt-4">
          <JobPacksTab clientSlug={clientSlug} readOnly={isAuditor} onCount={setPackCount} />
        </TabsContent>

        <TabsContent value="audits" className="pt-4">
          <div className="flex justify-end mb-3">
            <Button data-testid="ar-add-audit" className="brand-gradient text-white" onClick={() => setModal({ kind: "audit", edit: null })}>
              <Plus className="h-4 w-4 mr-1" /> Upload Internal Audit
            </Button>
          </div>
          {audits.length === 0 && <EmptyState label="No internal audits uploaded yet." onAdd={() => setModal({ kind: "audit", edit: null })} />}
          {groupByYear(audits, "audit_date").map(([year, rows]) => (
            <YearGroup key={year} year={year} count={rows.length}>
              {rows.map(a => (
                <RecordCard key={a.id} title={a.report_name || a.file?.filename || "Internal audit"}
                  subtitle={`${fmt(a.audit_date)} · ${a.auditor || "—"} · ${a.standard_scope || "—"}`}
                  next={a.next_due_date} due_soon={a.due_soon} overdue={a.overdue}
                  fileUrl={a.file ? `${API}/internal-audits/${a.id}/file` : null} fileName={a.file?.filename}
                  onEdit={() => setModal({ kind: "audit", edit: a })}
                  onDelete={() => del(`/internal-audits/${a.id}`)}
                  notes={a.notes} testid={`ar-audit-${a.id}`} />
              ))}
            </YearGroup>
          ))}
        </TabsContent>

        <TabsContent value="reviews" className="pt-4">
          <div className="flex justify-end mb-3">
            <Button data-testid="ar-add-review" className="brand-gradient text-white" onClick={() => setModal({ kind: "review", edit: null })}>
              <Plus className="h-4 w-4 mr-1" /> Upload Management Review
            </Button>
          </div>
          {reviews.length === 0 && <EmptyState label="No management reviews uploaded yet." onAdd={() => setModal({ kind: "review", edit: null })} />}
          {groupByYear(reviews, "meeting_date").map(([year, rows]) => (
            <YearGroup key={year} year={year} count={rows.length}>
              {rows.map(r => (
                <RecordCard key={r.id} title={r.minutes_name || r.file?.filename || "Management review"}
                  subtitle={`${fmt(r.meeting_date)} · ${r.standards_covered || "—"}`}
                  next={r.next_due_date} due_soon={r.due_soon} overdue={r.overdue}
                  fileUrl={r.file ? `${API}/management-reviews/${r.id}/file` : null} fileName={r.file?.filename}
                  onEdit={() => setModal({ kind: "review", edit: r })}
                  onDelete={() => del(`/management-reviews/${r.id}`)}
                  notes={r.notes} extra={r.attendees ? `Attendees: ${r.attendees}` : ""} testid={`ar-review-${r.id}`} />
              ))}
            </YearGroup>
          ))}
        </TabsContent>
      </Tabs>

      <RecordModal kind={modal.kind} edit={modal.edit} clientSlug={clientSlug}
        onClose={() => setModal({ kind: null, edit: null })} onSaved={() => { setModal({ kind: null, edit: null }); load(); }} />
    </div>
  );
}

function ReminderCard({ tone, icon, label, date, sub, overdue, soon, onAdd, testid }) {
  const t = tone === "indigo" ? "from-indigo-500 to-purple-600" : "from-cyan-500 to-teal-600";
  return (
    <Card className="glass-card overflow-hidden" data-testid={testid}>
      <div className={`h-1 bg-gradient-to-r ${t}`} />
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 text-slate-600 text-xs uppercase tracking-widest font-semibold">{icon} {label}</div>
          {(overdue > 0 || soon > 0) && (
            <div className="flex gap-1">
              {overdue > 0 && <Badge className="bg-rose-100 text-rose-800">{overdue} overdue</Badge>}
              {soon > 0 && <Badge className="bg-amber-100 text-amber-800">{soon} due soon</Badge>}
            </div>
          )}
        </div>
        <div className="mt-1 text-xl font-extrabold">{date ? fmt(date) : "—"}</div>
        <div className="text-xs text-slate-500">{sub}</div>
        <Button size="sm" variant="outline" className="mt-3" onClick={onAdd}><Plus className="h-3 w-3 mr-1" /> New record</Button>
      </CardContent>
    </Card>
  );
}

function YearGroup({ year, count, children }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-2"><span className="text-sm font-bold text-slate-700">{year}</span><span className="text-xs text-slate-400">{count} record{count === 1 ? "" : "s"}</span><div className="flex-1 border-b border-dashed border-slate-200 ml-2" /></div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function RecordCard({ title, subtitle, next, due_soon, overdue, fileUrl, fileName, onEdit, onDelete, notes, extra, testid }) {
  return (
    <Card className="glass-card" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-semibold truncate">{title}</div>
              {overdue && <Badge className="bg-rose-100 text-rose-800">Next audit overdue</Badge>}
              {!overdue && due_soon && <Badge className="bg-amber-100 text-amber-800">Next audit due soon</Badge>}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
            {extra && <div className="text-xs text-slate-500 mt-0.5">{extra}</div>}
            {next && <div className="text-xs text-slate-500 mt-0.5">Next due: <span className="font-mono">{fmt(next)}</span></div>}
            {notes && <div className="text-xs italic text-slate-600 mt-2 border-l-2 border-indigo-300 pl-2">"{notes}"</div>}
          </div>
          <div className="flex gap-2 shrink-0">
            {fileUrl && <a href={fileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs text-indigo-700 hover:bg-indigo-100"><Eye className="h-3 w-3" /> View</a>}
            {fileUrl && <a href={fileUrl} download={fileName} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs hover:bg-slate-50"><Download className="h-3 w-3" /> Download</a>}
            <Button size="sm" variant="outline" onClick={onEdit}><RefreshCcw className="h-3 w-3 mr-1" /> Edit / Replace</Button>
            <Button size="sm" variant="ghost" onClick={onDelete}><Trash2 className="h-3 w-3 text-rose-500" /></Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ label, onAdd }) {
  return (
    <div className="text-center py-10 border border-dashed rounded-xl bg-slate-50/50">
      <div className="text-sm text-slate-500 mb-3">{label}</div>
      <Button onClick={onAdd} className="brand-gradient text-white"><Plus className="h-4 w-4 mr-1" /> Upload first record</Button>
    </div>
  );
}

function RecordModal({ kind, edit, clientSlug, onClose, onSaved }) {
  const isAudit = kind === "audit";
  const empty = isAudit
    ? { audit_date: "", auditor: "", standard_scope: "", report_name: "", next_due_date: "", notes: "" }
    : { meeting_date: "", attendees: "", standards_covered: "", minutes_name: "", next_due_date: "", notes: "" };
  const [f, setF] = useState(empty);
  const [file, setFile] = useState(null);
  useEffect(() => {
    if (!kind) return;
    setF(edit ? { ...empty, ...edit } : empty);
    setFile(null);
    /* eslint-disable-next-line */
  }, [kind, edit]);

  if (!kind) return null;
  const dateField = isAudit ? "audit_date" : "meeting_date";
  const nameField = isAudit ? "report_name" : "minutes_name";
  const label = isAudit ? "Internal Audit" : "Management Review";

  const submit = async () => {
    if (!f[dateField]) { toast.error(`${isAudit ? "Audit" : "Meeting"} date required`); return; }
    const fd = new FormData();
    fd.append("client_slug", clientSlug);
    Object.entries(f).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") fd.append(k, v); });
    if (file) fd.append("file", file);
    try {
      if (edit?.id) {
        // Update metadata via PATCH; replace file separately if provided
        await api.patch(`/${isAudit ? "internal-audits" : "management-reviews"}/${edit.id}`, f);
        if (file) {
          const fd2 = new FormData();
          fd2.append("file", file);
          await apiPostForm(`/${isAudit ? "internal-audits" : "management-reviews"}/${edit.id}/replace-file`, fd2);
        }
      } else {
        await apiPostForm(`/${isAudit ? "internal-audits" : "management-reviews"}`, fd);
      }
      toast.success(edit ? "Updated" : "Uploaded");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ar-modal">
        <DialogHeader><DialogTitle>{edit ? `Edit ${label}` : `Upload ${label}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label={`${isAudit ? "Audit" : "Meeting"} date *`}>
              <Input type="date" value={f[dateField] || ""} onChange={e => setF({ ...f, [dateField]: e.target.value })} data-testid="ar-date" />
            </Field>
            <Field label="Next due date">
              <Input type="date" value={f.next_due_date || ""} onChange={e => setF({ ...f, next_due_date: e.target.value })} data-testid="ar-next-due" />
            </Field>
          </div>
          {isAudit ? (
            <>
              <Field label="Auditor"><Input value={f.auditor} onChange={e => setF({ ...f, auditor: e.target.value })} data-testid="ar-auditor" /></Field>
              <Field label="Standard / Scope"><Input value={f.standard_scope} onChange={e => setF({ ...f, standard_scope: e.target.value })} placeholder="e.g. ISO 9001 :2015 — Warehouse" /></Field>
            </>
          ) : (
            <>
              <Field label="Attendees"><Input value={f.attendees} onChange={e => setF({ ...f, attendees: e.target.value })} data-testid="ar-attendees" placeholder="Names of attendees, comma separated" /></Field>
              <Field label="Standards covered"><Input value={f.standards_covered} onChange={e => setF({ ...f, standards_covered: e.target.value })} placeholder="e.g. ISO 9001, ISO 14001" /></Field>
            </>
          )}
          <Field label={isAudit ? "Report name" : "Minutes / Report name"}>
            <Input value={f[nameField]} onChange={e => setF({ ...f, [nameField]: e.target.value })} placeholder="Defaults to uploaded filename" />
          </Field>
          <Field label={`PDF file ${edit ? "(optional — leave empty to keep existing)" : "*"} (≤25 MB)`}>
            <input type="file" accept="application/pdf,.pdf" onChange={e => setFile(e.target.files?.[0] || null)} className="block text-sm" data-testid="ar-file" />
            {file && <div className="text-xs text-slate-500 mt-1">Selected: {file.name} ({(file.size / 1024).toFixed(0)} KB)</div>}
            {edit?.file?.filename && !file && <div className="text-xs text-slate-500 mt-1">Existing: {edit.file.filename}</div>}
          </Field>
          <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} /></Field>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="ar-save" className="brand-gradient text-white" onClick={submit}>{edit ? "Save" : "Upload"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) { return <div><Label className="text-xs">{label}</Label>{children}</div>; }
